import {
  Calendar,
  Clock,
  MessageSquare,
  Phone,
  Sparkles,
  UtensilsCrossed,
} from 'lucide-react';

import { IndustryPage, type IndustryPageConfig } from '@/components/marketing/industry-page';

const config: IndustryPageConfig = {
  seoTitle: 'AI Receptionist for Restaurants & Hospitality — Reservations & FAQs, 24/7',
  seoDescription:
    'Answer every reservation call, handle opening hours questions, and capture large group bookings — even during service. AI receptionist for restaurants and hospitality, 24/7.',
  path: '/industries/hospitality',
  badge: 'Restaurants & Hospitality',
  headline: 'Reservations taken and questions answered — even during dinner service.',
  subheadline:
    'Your AI receptionist handles booking calls, opening hours, menu questions, and large group inquiries — freeing your floor staff to focus on the guests in front of them.',
  pains: [
    'Floor staff can\'t step away from tables to answer the phone during a busy service.',
    'Opening hours and menu questions clog the phone line during peak periods.',
    'Large group and private dining inquiries get lost when staff are in the weeds.',
  ],
  features: [
    {
      icon: Phone,
      title: '24/7 call answering',
      desc: 'Every call answered during service, after close, and on public holidays — even when your team is at full stretch.',
    },
    {
      icon: Calendar,
      title: 'Reservation capture',
      desc: 'Name, party size, date, time, and any dietary requirements — captured and logged for your host to confirm.',
    },
    {
      icon: UtensilsCrossed,
      title: 'Menu & FAQ answers',
      desc: 'Opening hours, parking, BYO policy, allergen information, private dining — answered from your knowledge base instantly.',
    },
    {
      icon: Sparkles,
      title: 'Large group & event inquiries',
      desc: 'Captures function inquiries with guest count, date, and budget. Notifies your events team to follow up.',
    },
    {
      icon: MessageSquare,
      title: 'Website chat on same brain',
      desc: 'Phone and website chat powered by the same knowledge base — consistent answers across every channel.',
    },
    {
      icon: Clock,
      title: 'After-hours reservation capture',
      desc: 'Guests book at 11pm. Your AI captures the request; your host team confirms in the morning.',
    },
  ],
  faqs: [
    {
      q: 'Can it integrate with my reservation system (OpenTable, Resy)?',
      a: 'The agent captures reservation details into your dashboard. Direct reservation system integration is on the roadmap.',
    },
    {
      q: 'Can I customise it for events and private dining?',
      a: 'Yes — add your function packages, minimum spend requirements, and event lead times to the knowledge base.',
    },
    {
      q: 'What if a caller wants to change or cancel a booking?',
      a: 'The agent captures the change request and logs it with the original booking reference. Your team confirms the change.',
    },
    {
      q: 'Can it handle multiple venues?',
      a: 'Yes — configure separate knowledge bases and phone numbers for each venue, all managed from one dashboard.',
    },
    {
      q: 'Does it work for cafés and quick-service as well as fine dining?',
      a: 'Yes — any hospitality business with a phone line benefits. Configure the tone and content to match your venue.',
    },
  ],
  ctaHeadline: 'Focus on the guests in front of you.',
  ctaSub: 'AI receptionist for hospitality. Live in under 10 minutes.',
};

export function HospitalityIndustryPage(): JSX.Element {
  return <IndustryPage config={config} />;
}
